import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { Form as SpikeForm, SendCondition } from '@spike/form-model';
import { listFormsThunk } from '@spike/forms-action';
import Button from 'components/UI/V3/Button/Button';
import { ItemSelectionDrawer } from 'components/UI/V3/ItemSelectionDrawer/ItemSelectionDrawer';
import AddEditForm from 'components/Forms/Form/AddEditForm';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import {
    AssociatedForm,
    Form,
    createEmptyForm,
    getFormSendConditionDescription,
    isAutomaticSendCondition
} from 'model/Forms';
import { RootState } from 'store';
import clsx from 'clsx';
import styles from './ServiceFormsSection.module.scss';

type ServiceCategory = 'grooming' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training';

interface Props {
    forms: AssociatedForm[];
    serviceId?: number;
    serviceCategory?: ServiceCategory;
    disabled?: boolean;
    className?: string;
    onChange: (forms: AssociatedForm[]) => void;
}

const toAssociatedForm = (form: SpikeForm | AssociatedForm | Form): AssociatedForm => ({
    id: Number(form.id),
    title: form.title,
    sendCondition: String(form.sendCondition || ''),
    active: 'active' in form ? Boolean(form.active) : true
});

export const ServiceFormsSection: React.FC<Props> = ({
    forms,
    serviceId,
    serviceCategory,
    disabled,
    className,
    onChange
}) => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper() as any;
    const availableForms: SpikeForm[] = useSelector((state: RootState) => state.forms.forms) || [];
    const formsRef = useRef(forms);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSelected, setTempSelected] = useState<AssociatedForm[]>(forms);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createFormDraft, setCreateFormDraft] = useState<ReturnType<typeof createEmptyForm> | null>(null);

    useEffect(() => {
        formsRef.current = forms;
    }, [forms]);

    const refreshForms = () =>
        Promise.resolve(
            dispatch((listFormsThunk as any)(apiClientWrapper, { page: 1, per: 200, status: 'active' }))
        ).catch(() => {
            // Drawer still works with already associated forms if the list request fails.
        });

    useEffect(() => {
        void refreshForms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasNoAssociatedForms = forms.length === 0;

    const drawerItems = useMemo(
        () =>
            availableForms
                .filter(form => form.id && isAutomaticSendCondition(String(form.sendCondition || '')))
                .map(toAssociatedForm),
        [availableForms]
    );

    const hasSelectableForms = drawerItems.length > 0;
    const showAddForms = hasSelectableForms || !hasNoAssociatedForms;

    const filteredItems = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return drawerItems;
        return drawerItems.filter(form => form.title.toLowerCase().includes(query));
    }, [drawerItems, searchQuery]);

    const getFormRule = (form: AssociatedForm) => {
        const fromForm = getFormSendConditionDescription(form.sendCondition);
        if (fromForm) return fromForm;

        const catalogForm = availableForms.find(item => Number(item.id) === form.id);
        return getFormSendConditionDescription(String(catalogForm?.sendCondition || ''));
    };

    const openCreateForm = () => {
        if (disabled || !marketplace.id) return;
        setCreateFormDraft(
            createEmptyForm(marketplace.id, 'other', {
                sendCondition: SendCondition.FirstBooking,
                serviceIds: serviceId ? [serviceId] : []
            })
        );
        setShowCreateForm(true);
    };

    const assignFormToService = (saved: Form) => {
        const associated = toAssociatedForm(saved);
        if (!associated.id || !isAutomaticSendCondition(associated.sendCondition)) return;

        const current = formsRef.current;
        if (current.some(form => form.id === associated.id)) return;

        const next = [...current, associated];
        formsRef.current = next;
        onChange(next);
    };

    const openDrawer = () => {
        if (disabled) return;
        setTempSelected(forms);
        setSearchQuery('');
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setSearchQuery('');
        setTempSelected(forms);
    };

    const applySelection = () => {
        const unique = tempSelected.filter(
            (form, index, list) =>
                isAutomaticSendCondition(form.sendCondition) && list.findIndex(item => item.id === form.id) === index
        );
        onChange(unique);
        setIsDrawerOpen(false);
    };

    const removeForm = (formId: number) => {
        if (disabled) return;
        onChange(forms.filter(form => form.id !== formId));
    };

    const handleFormSaved = (saved: Form) => {
        assignFormToService(saved);
        void refreshForms();
    };

    const handleCloseCreateForm = () => {
        setShowCreateForm(false);
        setCreateFormDraft(null);
        void refreshForms();
    };

    return (
        <div className={clsx(styles.section, className)}>
            <h3 className={styles.title}>Forms</h3>
            <p className={styles.description}>Select the forms that need to be completed for this service.</p>

            {hasNoAssociatedForms ? (
                <p className={styles.emptyMessage}>
                    {hasSelectableForms ? 'No forms associated with this service yet.' : 'No forms created yet.'}
                </p>
            ) : (
                <div className={styles.list}>
                    {forms.map(form => (
                        <div
                            key={form.id}
                            className={styles.item}
                        >
                            <div className={styles.itemCopy}>
                                <span className={styles.itemTitle}>{form.title}</span>
                                <span className={styles.itemSubtitle}>{getFormRule(form)}</span>
                            </div>
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => removeForm(form.id)}
                                disabled={disabled}
                                aria-label={`Remove ${form.title}`}
                            >
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className={styles.removeIcon}
                                />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.actions}>
                {showAddForms && (
                    <Button
                        label="Add Forms"
                        variant="green"
                        className={styles.actionButton}
                        disabled={disabled}
                        onClick={openDrawer}
                    />
                )}
                <Button
                    label="Create New Form"
                    variant="green"
                    className={styles.actionButton}
                    disabled={disabled}
                    onClick={openCreateForm}
                />
            </div>

            <ItemSelectionDrawer<AssociatedForm>
                isOpen={isDrawerOpen}
                title="Select Forms"
                items={drawerItems}
                selectedItems={forms}
                tempSelectedItems={tempSelected}
                searchQuery={searchQuery}
                filteredItems={filteredItems}
                selectAllLabel="All Forms"
                searchPlaceholder="Search by form name"
                applyLabel="Add"
                variant="forms"
                itemDisplay={{
                    getTitle: item => item.title,
                    getSubtitle: item => getFormSendConditionDescription(item.sendCondition),
                    getPrice: () => ''
                }}
                onClose={closeDrawer}
                onSearchChange={setSearchQuery}
                onTempSelectionChange={setTempSelected}
                onApply={applySelection}
                onCancel={closeDrawer}
            />

            {showCreateForm && createFormDraft && (
                <AddEditForm
                    isActive={showCreateForm}
                    form={createFormDraft}
                    mode="add"
                    assignServiceId={serviceId}
                    serviceCategory={serviceCategory}
                    onClose={handleCloseCreateForm}
                    onSaved={handleFormSaved}
                />
            )}
        </div>
    );
};

export default ServiceFormsSection;
